/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("HostService", ["InitService", "DebugService", "EventListener"], function (context, cls) {
  /**
   * Main service which init global gbc listeners
   * @namespace gbc.HostService
   * @gbcService
   */
  class HostService /** @lends gbc.HostService */ {
    constructor() {
      this.currentWindowChanged = "currentWindowChanged";

      /** @type classes.MainContainerWidget */
      this._widget = null;
      /** @type String */
      this._defaultTitle = "";
      /** @type classes.ApplicationHostWidget */
      this._applicationHostWidget = null;
      /** @type classes.ApplicationHostSettingsWidget */
      this._settingPanel = null;
      /** @type classes.WindowNode */
      this._currentWindowNode = null;
      /** @type classes.WindowWidget */
      this._currentWindowWidget = null;
      /** @type classes.EventListener */
      this._eventListener = null;
      /** @type HTMLElement */
      this._dropDownContainer = null;
      /** @type HTMLElement */
      this._fontPreloader = null;
      /** @type Array **/
      this._windowStack = null;
      /** @type String */
      this._currentMediaSize = null;
      /**
       * @type {HandleRegistration}
       */
      this._browserResizeHandler = null;

      /** @type Object **/
      this._hostSize = null;

      /** @type String **/
      this._windowState = null;

      /** @type Boolean **/
      this._forceDefaultSettings = null;
      /**
       * @type {WeakMap<classes.WindowWidget, classes.WindowNode>}
       */
      this._widgetToNodeWindows = null;
    }

    init() {
      this._eventListener = new cls.EventListener();
      this._windowStack = [];
      this._widgetToNodeWindows = new WeakMap();

      const existingOnError = window.onerror;
      window.onerror = function (msg, file, line, col, error) {
        if (existingOnError) {
          existingOnError(msg, file, line, col, error);
        }
        context.HostService._eventListener.emit("error", error, msg, file, line, col);
        return false;
      };

      // Device orientation handler
      window.addEventListener(
        "orientationchange",
        function () {
          if (cls.WindowHelper.isIOS()) {
            const viewportSelector = document.querySelector("meta[name=viewport]");
            if (viewportSelector) {
              const content = viewportSelector.getAttribute("content");
              //GMI-911: temporarily limiting the zoom to 1.0 to avoid
              //scaling too large on orientation change
              viewportSelector.setAttribute("content", `${content}, maximum-scale=1.0`);
              // Using setTimeout since iOS doesn't seem to relayout everytime there is a rotation change.
              // No events are sent to the VM, nor layout executed
              window.setTimeout(() => {
                //reset to zoomable after a while
                viewportSelector.setAttribute("content", content);
              }, 90);
            }
          }
          if (window.isMobile) {
            this._eventListener.emit("orientationChange.Host");
          }
        }.bind(this)
      );

      // Keep track of host size on resize
      this._hostSize = {
        width: 0,
        height: 0
      };

      // Window Resize handler
      window.addEventListener(
        "resize",
        function () {
          let screenSizeChanged = {
            width: this._hostSize.width !== window.document.body.offsetWidth,
            height: this._hostSize.height !== window.document.body.offsetHeight
          };
          this._hostSize = {
            width: window.document.body.offsetWidth,
            height: window.document.body.offsetHeight
          };
          // emit new size and changes in host size
          this._eventListener.emit("resize.Host", window.document.body.offsetWidth, window.document.body.offsetHeight, screenSizeChanged);
        }.bind(this)
      );

      // Let know the VM when the screen is resized
      this.onScreenResize(cls.FunctionHelper.debounce(this._notifyVMOnScreenResize.bind(this), 250));
      this.onScreenResize(() => this.updateDisplay());
    }

    destroy() {
      if (this._settingPanel) {
        this.hideSettingsPanel();
      }
      if (this._settingsPanelCloseHandler) {
        this._settingsPanelCloseHandler();
        this._settingsPanelCloseHandler = null;
      }
      if (this._browserResizeHandler) {
        this._browserResizeHandler();
        this._browserResizeHandler = null;
      }
      this._eventListener.destroy();
    }

    isLogPlayerRequested() {
      return cls.WindowHelper.isURLParameterEnabled("logplayer");
    }

    preStart() {
      if (!context.DebugService.isMonitorWindow()) {
        this._widget = cls.WidgetFactory.createWidget("MainContainer", {
          appHash: gbc.systemAppId
        });
        let mainWidget = this._widget;
        if (this.isLogPlayerRequested()) {
          mainWidget = context.LogPlayerService.createWidget(this._widget);
        }

        this._defaultTitle = document.title;
        this._applicationHostWidget = cls.WidgetFactory.createWidget("ApplicationHost", {
          appHash: gbc.systemAppId
        });
        this._widget.addChildWidget(this._applicationHostWidget);

        // create drop down container
        if (!this._dropDownContainer) {
          // This require to rewrite a lot of tests
          this._dropDownContainer = document.createElement("div");
          this._dropDownContainer.classList.add("gbc_DropDownContainer", "hidden");
          document.body.appendChild(this._dropDownContainer);
        }
        // preload mono font
        if (!this._fontPreloader) {
          this._fontPreloader = document.createElement("span");
          this._fontPreloader.textContent = "M";
          this._fontPreloader.classList.add("gbc_MonoFontPreloader");
          document.body.appendChild(this._fontPreloader);
        }

        const mainContainerElement = document.querySelector("#mainContainer");
        if (mainContainerElement) {
          // Vite template use id element, delete the main container
          mainContainerElement.remove();
        }
        // append the main element to body
        document.body.appendChild(mainWidget.getElement());
        //}
      }
    }

    /**
     * Return the main container widget. Root of GBC
     * @returns classes.MainContainerWidget
     */
    getWidget() {
      return this._widget;
    }

    /**
     * Return the DOM Element created to host drop down widget
     * @returns {Element}
     */
    getDropDownContainer() {
      return this._dropDownContainer;
    }

    /**
     *
     * @returns {classes.ApplicationHostWidget}
     */
    getApplicationHostWidget() {
      return this._applicationHostWidget;
    }

    start() {
      const params = context.UrlService.currentUrl().getQueryStringObject();

      if (window.gbcWrapper.isProtocolTypeUA() && (params.app || context.bootstrapInfo.appName)) {
        context.SessionService.startApplication(params.app || context.bootstrapInfo.appName);
      } else {
        this.displayNoSession();
      }
    }

    displaySession() {
      this._applicationHostWidget.getLauncher().setHidden(true);
      // Init GBC specific menu items
      context.ChromeActionService.createUpdateGbcMenu();
    }
    displayNoSession() {
      this._applicationHostWidget.getLauncher().setHidden(false);
    }
    whenError(cb) {
      this._eventListener.when("error", cb);
    }
    /**
     *
     * @param {number?} appHash
     * @returns {classes.WindowNode}
     */
    getCurrentWindowNode(appHash) {
      if (typeof appHash === "number" && appHash >= 0) {
        return context.SessionService.getCurrent().getApplicationByHash(appHash).getUI().getCurrentWindow();
      }
      return this._currentWindowNode;
    }
    /**
     * @param {number?} appHash
     * @returns {classes.WindowWidget}
     */
    getCurrentWindowWidget(appHash) {
      const windowNode = this.getCurrentWindowNode(appHash);
      return windowNode && windowNode.getWidget();
    }
    /**
     */
    unsetCurrentWindowNode() {
      this._currentWindowNode = null;
    }

    /**
     * Defines the initial state of the window (UR only).
     * @param {String} state - state of the window
     */
    setWindowState(state) {
      this._windowState = state;
    }
    /**
     * Get the state of the window as defined in the 4ST
     * @return {String}
     */
    getWindowState() {
      return this._windowState || "normal";
    }

    /**
     * Sets the value of the ForceDefaultSettings variable as defined in the 4ST
     * @param {Boolean} val
     */
    setForceDefaultSettings(val) {
      this._forceDefaultSettings = val;
    }

    /**
     * Gets the value of the ForceDefaultSettings variable as defined in the 4ST
     * @return {Boolean}
     */
    getForceDefaultSettings() {
      return this._forceDefaultSettings;
    }

    /**
     *
     * @param {Hook} hook
     * @param {boolean} [once] - if true, will only fire once
     * @return {HandleRegistration}
     */
    onCurrentWindowChange(hook, once = false) {
      return this._eventListener.when(this.currentWindowChanged, hook, once);
    }

    /**
     * Invalidate the layout of the Host Widget, and Application Widget
     * Call a layout command
     * Refresh the ChromeActions
     */
    updateDisplay() {
      if (!this._applicationHostWidget) {
        return;
      }
      const session = context.SessionService.getCurrent();
      let app = session?.getCurrentApplication();
      if (!app?.scheduler) {
        return;
      }
      app.scheduler.layoutCommand({
        resize: true
      });

      context.ChromeActionService.refresh();
    }

    /**
     * - Manage switch of window in the DOM depending on whether they are modal or not and having WebComponent or not.
     * - Display/hide topmenu & toolbars of active/inactive windows.
     * - Set window title
     * @param {classes.WindowNode} windowToDisplay
     */
    setDisplayedWindowNode(windowToDisplay) {
      // Note : Not sure we need to trigger it at the start of this method
      this.updateDisplay();

      const session = context.SessionService.getCurrent();
      if (!session) {
        return;
      }
      // Do nothing if nothing to display
      // WARNING: Adding a return if windowToDisplay === this._currentWindowNode (which seems logical and optimize multiple calls of this function) generates issues : for example GBC-1709 will fail
      if (!windowToDisplay) {
        return;
      }
      const app = session.getCurrentApplication(),
        currentWindowWidget = this._currentWindowNode?.getWidget(),
        currentWindowUIWidget = currentWindowWidget?.getUserInterfaceWidget(),
        windowToDisplayWidget = windowToDisplay.getWidget(),
        windowToDisplayIsModal = windowToDisplayWidget?.isModal,
        windowToDisplayUIWidget = windowToDisplayWidget?.getUserInterfaceWidget(),
        switchingApplication = windowToDisplayUIWidget !== currentWindowUIWidget;
      let previousWindowWidget = windowToDisplayUIWidget.getActiveWindowWidget();
      // determine if in current application a switch of window occurred
      // if only a switch of application occurred without change of window in current app, we do nothing (application management done by SessionWidget did all the job)

      // If switching of application, remove from DOM previous application widget (if not tabbed container)
      if (switchingApplication) {
        this._displayWindowNodeOnApplicationSwitching(windowToDisplay);
      }
      // Then Update window widget :
      // - if normal window, just remove previous window widget from DOM
      // - if modal, we don't remove previous window from DOM but ensure we display the top normal parent window
      if (windowToDisplayIsModal) {
        this._displayModalWindowNode(windowToDisplay);
      } else {
        this._displayNotModalWindowNode(windowToDisplay);
      }
      // and add new window widget in the DOM
      windowToDisplayWidget.addInDom();

      if (!previousWindowWidget || previousWindowWidget.isDestroyed()) {
        previousWindowWidget = this.getPreviousWindowOfStack(windowToDisplay)?.getWidget();
      }

      windowToDisplayWidget?._toolBarWidget?.setHidden(false);

      if (this._currentWindowNode && this._currentWindowNode !== windowToDisplay) {
        // emit disable on previous displayed window
        this._currentWindowNode.getWidget()?.emit(context.constants.widgetEvents.disable);
      }
      this._currentWindowNode = windowToDisplay;
      windowToDisplay.getApplication().getUI().setCurrentWindow(windowToDisplay.getId());
      windowToDisplayUIWidget.setActiveWindowWidget(windowToDisplay.isDestroyed() ? null : windowToDisplay.getWidget());

      // send activate signal to inform elements that window is append to DOM
      windowToDisplayUIWidget.activate(windowToDisplayWidget);

      if (windowToDisplay.getWidget()) {
        this._invalidateAllocatedSpaceOnWindowDisplay(windowToDisplay, previousWindowWidget, switchingApplication);
      }
      if (switchingApplication || windowToDisplayWidget !== previousWindowWidget) {
        this._eventListener.emit(this.currentWindowChanged, windowToDisplayWidget);
        previousWindowWidget?.disable();
        windowToDisplayWidget.activate();
      }
      // need to refresh current application layout for potential background dynamic VM update
      app?.dvm?.updateProcessingStatus();
      app?.scheduler.layoutCommand();
      app?.scheduler.restoreFocusCommand();
    }

    /**
     * @private
     * @param {classes.WindowNode} windowToDisplay
     */
    _displayWindowNodeOnApplicationSwitching(windowToDisplay) {
      context.DebugService.onApplicationSwitch();

      const session = context.SessionService.getCurrent();
      const targetApplication = windowToDisplay.getApplication();

      // Keep the direct owner visible when a child modal is displayed
      let keptApplication = null;
      if (windowToDisplay.isModal() && targetApplication.isAwaited()) {
        keptApplication = session.getNavigationManager().getParentApplication(targetApplication);
      }

      // Hide every other application to keep state classes (current/processing/out_of_view) in sync even with embedded content
      for (const app of session.getApplications()) {
        if (app === targetApplication || app === keptApplication) {
          continue;
        }
        const appWidget = app.getUI()?.getWidget();
        if (appWidget) {
          appWidget.removeFromDom();
        }
      }
    }

    /**
     * @private
     * @param {classes.WindowNode} windowToDisplay
     */
    _displayNotModalWindowNode(windowToDisplay) {
      // remove all other windows from DOM, we want only windowToDisplay
      for (const winWidget of windowToDisplay.getWidget().getUserInterfaceWidget().getChildren()) {
        if (winWidget !== windowToDisplay.getWidget() && winWidget instanceof cls.WindowWidget) {
          winWidget.removeFromDom();
        }
      }
    }

    /**
     * @private
     * @param {classes.WindowNode} windowToDisplay
     */
    _displayModalWindowNode(windowToDisplay) {
      // if we display a modal need to display also the previous non modal window
      const uiWidget = windowToDisplay.getWidget().getUserInterfaceWidget();
      const uiChildrenWindows = uiWidget.getChildren();
      let windowToDisplayIndex = uiChildrenWindows.indexOf(windowToDisplay.getWidget());
      const windowToDisplayParentId = windowToDisplay.getParentWindowId();
      let nonModalPreviousWindow = null;

      // Get the previous non-modal window to display it
      let previousWindowFound = false;
      while (!previousWindowFound && windowToDisplayIndex >= 0) {
        nonModalPreviousWindow = uiChildrenWindows[windowToDisplayIndex - 1];
        const previousIsModalWindow = nonModalPreviousWindow instanceof cls.WindowWidget && !nonModalPreviousWindow.isModal;
        previousWindowFound = previousIsModalWindow && nonModalPreviousWindow._auiTag === windowToDisplayParentId;
        windowToDisplayIndex -= 1;
      }
      if (nonModalPreviousWindow && nonModalPreviousWindow !== uiWidget.getActiveWindowWidget()) {
        nonModalPreviousWindow.addInDom();
        nonModalPreviousWindow._forceVisible = true;
      }
    }

    /**
     * @private
     * @param {classes.WindowNode} windowToDisplay
     * @param {classes.WindowWidget} previousWindowWidget
     * @param {boolean} switchingApplication
     */
    _invalidateAllocatedSpaceOnWindowDisplay(windowToDisplay, previousWindowWidget, switchingApplication) {
      const app = context.SessionService.getCurrent().getCurrentApplication();
      const windowWidget = windowToDisplay.getWidget();
      const isModal = windowWidget.isModal;
      // if we switched application we need to invalidate allocated space
      if (previousWindowWidget && switchingApplication) {
        windowToDisplay.getWidget().getLayoutEngine().invalidateAllocatedSpace();
      }

      if (!isModal || !app) {
        context.SessionService.getCurrent().getNavigationManager().setCurrentWindow(windowToDisplay);
      }
    }

    unsetDisplayedWindowNode() {
      this.setDisplayedWindowNode(null);
    }

    /**
     * Define the current app title to display
     * @param {string} title - text to display in top bar
     * @param {classes.Application} app -
     */
    setDocumentTitle(title, app) {
      if (document.title === title || !app) {
        return;
      }
      document.title = title || this._defaultTitle;
      context.__wrapper.nativeCall(
        context.__wrapper.param(
          {
            name: "windowTitle",
            args: {
              title: document.title
            }
          },
          app
        )
      );
    }

    /**
     * Register a new window
     * Bind the close button onto the new window
     * @param {classes.WindowNode} windowNode
     * @param {classes.WidgetBase} widget
     */
    registerClosableWindow(windowNode, widget) {
      const windowWidget = widget || windowNode?.getWidget();
      // Add a window to the stack
      if (this._windowStack.indexOf(windowNode) < 0) {
        this._windowStack.push(windowNode);
      }
      this._widgetToNodeWindows.set(windowWidget, windowNode);

      this._setCurrentWindowWidget(windowWidget);
    }

    unregisterClosableWindow(windowNode) {
      const windowWidget = windowNode?.getWidget(),
        windowUuid = windowWidget?.getUniqueIdentifier();
      this._widgetToNodeWindows.delete(windowWidget);
      // Remove window from the stack
      if (this._windowStack.indexOf(windowNode) >= 0) {
        this._windowStack.splice(this._windowStack.indexOf(windowNode), 1);
      }

      this._setCurrentWindowWidget(null);
    }

    /**
     * Show or hide the close action
     * @param {classes.WindowWidget} windowWidget The window widget that trigger the action
     * @param {boolean} closeActionActive Is the windowWidget require a close action
     */
    setClosableWindowActionActive(windowWidget, closeActionActive) {
      // Show or hide the close button of the chromebar
      let shouldHideButton = !closeActionActive || windowWidget.isModal;
      const closeWidget = context.ChromeActionService.getGbcMenuItem("close");
      // if asked by the wrapper to hide chrome icon from the chromebar, then always hide it
      if (!window.gbcWrapper.isCloseButtonVisibleInChromeBar()) {
        closeWidget?.setHidden(true);
      } else {
        closeWidget?.setHidden(shouldHideButton);
      }
      // hide quick close from applist as well if close action not active
      const windowNode = this._widgetToNodeWindows.get(windowWidget);
      if (windowNode) {
        context.ApplicationListService.setAppClosable(windowNode.getApplication(), !shouldHideButton);
      }

      this._setCurrentWindowWidget(windowWidget);
    }

    /**
     * Handler when the screen orientation changed
     * @param {function} callback - method to call once screen orientation changes
     * @note mobile only
     */
    onOrientationChange(callback) {
      return this._eventListener.when("orientationChange.Host", callback.bind(this));
    }

    /**
     * Handler when the screen size changed
     * @param {function} callback - method to call once screen size changes
     */
    onScreenResize(callback) {
      return this._eventListener.when("resize.Host", callback.bind(this));
    }

    /**
     *
     * @param {classes.WindowNode} windowToDisplay
     */
    getPreviousWindowOfStack(windowToDisplay) {
      let newIndex = this._windowStack.indexOf(windowToDisplay) - 1;
      newIndex = newIndex >= 0 ? newIndex : 0;
      return this._windowStack[newIndex];
    }

    /**
     * Create and add the settings panel to the DOM
     * If a setting panel already exist, it does nothing
     */
    showSettingsPanel(buildParameters) {
      if (this._settingPanel) {
        return;
      }
      this._settingPanel = cls.WidgetFactory.createWidget("ApplicationHostSettings", buildParameters);
      document.body.appendChild(this._settingPanel.getElement());
      this._settingsPanelCloseHandler = this._settingPanel.when(context.constants.widgetEvents.close, this.hideSettingsPanel.bind(this), true);
      this._settingPanel.show();
    }

    /**
     * Remove the setting panel from the dom
     */
    hideSettingsPanel() {
      if (!this._settingPanel) {
        return;
      }
      this._settingPanel.destroy();
      this._settingPanel = null;
    }

    _setCurrentWindowWidget(windowWidget) {
      this._currentWindowWidget = windowWidget;
    }

    closeCurrentWindow() {
      if (this._currentWindowWidget?.isClosable()) {
        this._currentWindowWidget?.emitClose();
      }
    }

    /**
     * Create a VM Event with all the media data
     * @private
     */
    _notifyVMOnScreenResize() {
      const mediaString = context.ThemeService.getMediaString();
      if (mediaString === this._currentMediaSize) {
        return;
      }
      const session = context.SessionService.getCurrent();
      if (!session || session.isEmpty()) {
        return;
      }
      let event = new cls.VMConfigureEvent(0, {
        media: context.ThemeService.getMediaString()
      });
      this._currentMediaSize = mediaString;
      for (const app of session.getApplications()) {
        let focusNode = app.getFocusedVMNode();
        if (focusNode && focusNode.getCurrentValueNode) {
          focusNode = focusNode.getCurrentValueNode();
        }

        const controller = focusNode ? focusNode.getController() : null;
        const newVmFocusWidget = controller ? controller.getWidget() : null;

        if (newVmFocusWidget && newVmFocusWidget.getValue) {
          controller.sendWidgetCursors();
          controller.sendWidgetValue(newVmFocusWidget.getValue());
        }

        app.scheduler.eventVMCommand(event, app.getNode(0));
        gbc.LogService.ui.log("resize", context.ThemeService.getMediaString());
      }
    }
  }

  gbc.HostService = new HostService();

  context.InitService.register(context.HostService);
});
